import { useState, useRef, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  FaPaperclip,
  FaTimes,
  FaPaperPlane,
  FaSpinner,
  FaImage,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileArchive,
  FaFile,
  FaFileVideo,
  FaFileAudio,
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaLink,
  FaQuoteRight,
  FaHeading,
  FaCode,
  FaChevronDown,
  FaSearch,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import * as mailService from "../../services/mailService";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
const { MAX_ATTACHMENTS, MAX_FILE_SIZE_MB } = mailService;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (file) => {
  const type = file.type || "";
  const name = file.name?.toLowerCase() || "";
  if (type.startsWith("image/")) return <FaImage className="text-blue-500" />;
  if (type === "application/pdf" || name.endsWith(".pdf")) return <FaFilePdf className="text-red-500" />;
  if (type.includes("word") || name.endsWith(".doc") || name.endsWith(".docx")) return <FaFileWord className="text-blue-600" />;
  if (type.includes("excel") || type.includes("spreadsheet") || name.endsWith(".xls") || name.endsWith(".xlsx")) return <FaFileExcel className="text-green-600" />;
  if (type.includes("zip") || type.includes("rar") || type.includes("compressed")) return <FaFileArchive className="text-yellow-600" />;
  if (type.startsWith("video/")) return <FaFileVideo className="text-purple-500" />;
  if (type.startsWith("audio/")) return <FaFileAudio className="text-pink-500" />;
  return <FaFile className="text-gray-400" />;
};

const EditorToolbar = ({ editor, htmlMode, onToggleHtml }) => {
  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt("URL de la imagen:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const setLink = () => {
    const url = window.prompt("URL del enlace:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-100 flex-wrap">
      {!htmlMode && (
        <>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("bold") ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Negrita"
      >
        <FaBold className="text-xs" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("italic") ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Cursiva"
      >
        <FaItalic className="text-xs" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("underline") ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Subrayado"
      >
        <FaUnderline className="text-xs" />
      </button>
      <span className="w-px h-4 bg-gray-200 mx-0.5" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("heading", { level: 2 }) ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Título"
      >
        <FaHeading className="text-xs" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("bulletList") ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Lista"
      >
        <FaListUl className="text-xs" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("orderedList") ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Lista numerada"
      >
        <FaListOl className="text-xs" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("blockquote") ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Cita"
      >
        <FaQuoteRight className="text-xs" />
      </button>
      <span className="w-px h-4 bg-gray-200 mx-0.5" />
      <button
        type="button"
        onClick={setLink}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive("link") ? "bg-gray-200 text-orange-600" : "text-gray-600"}`}
        title="Enlace"
      >
        <FaLink className="text-xs" />
      </button>
      <button
        type="button"
        onClick={addImage}
        className="p-2 rounded hover:bg-gray-200 text-gray-600"
        title="Insertar imagen (URL)"
      >
        <FaImage className="text-xs" />
      </button>
      <span className="w-px h-4 bg-gray-200 mx-0.5" />
        </>
      )}
      <button
        type="button"
        onClick={onToggleHtml}
        className={`p-2 rounded hover:bg-gray-200 ${htmlMode ? "bg-orange-100 text-orange-600" : "text-gray-600"}`}
        title={htmlMode ? "Volver al editor visual" : "Editar HTML directamente"}
      >
        <FaCode className="text-xs" />
      </button>
    </div>
  );
};

const ComposeModal = ({ open, onClose, onSend, replyData, accounts = [], connectedAccounts = {}, firmaTrabajador }) => {
  const connectedList = accounts.filter((a) => connectedAccounts[a.id]);

  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [firmaCuenta, setFirmaCuenta] = useState(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState("");
  const [incluirFirma, setIncluirFirma] = useState(true);
  const [tipoFirma, setTipoFirma] = useState("cuenta"); // "cuenta" | "trabajador"
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchText, setAccountSearchText] = useState("");
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const accountDropdownRef = useRef(null);

  const hasFirmaCuenta = firmaCuenta?.trim();
  const hasFirmaTrabajador = firmaTrabajador?.trim();
  const firmaSeleccionada = tipoFirma === "cuenta" ? firmaCuenta : firmaTrabajador;
  const firma = firmaSeleccionada?.trim() || null;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { class: "max-w-full h-auto rounded-lg" },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-orange-500 underline" },
      }),
      Placeholder.configure({
        placeholder: "Escribe tu mensaje aquí... Puedes usar formato, enlaces e imágenes.",
      }),
    ],
    content: "",
    editable: open,
    editorProps: {
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = () => {
                editorRef.current?.chain().focus().setImage({ src: reader.result }).run();
              };
              reader.readAsDataURL(file);
              event.preventDefault();
              return true;
            }
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const file = files[0];
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = () => {
            editorRef.current?.chain().focus().setImage({ src: reader.result }).run();
          };
          reader.readAsDataURL(file);
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (open) {
      const list = accounts.filter((a) => connectedAccounts[a.id]);
      const defaultId = replyData?.defaultAccountId;
      const validDefault = defaultId && list.some((a) => a.id === defaultId);
      setSelectedAccountId(validDefault ? defaultId : list[0]?.id || null);
      setTo(replyData?.to || "");
      setCc(replyData?.cc || "");
      setBcc(replyData?.bcc || "");
      setSubject(replyData?.subject || "");
      setAttachments([]);
      setDragOver(false);
      setHtmlMode(false);
      setIncluirFirma(true);
      if (editor?.commands) {
        editor.commands.setContent(replyData?.html || "");
        editor.setEditable(true);
      }
    }
  }, [open, replyData, editor, accounts, connectedAccounts]);

  useEffect(() => {
    setTipoFirma(firmaCuenta?.trim() ? "cuenta" : firmaTrabajador?.trim() ? "trabajador" : "cuenta");
  }, [firmaCuenta, firmaTrabajador]);

  useEffect(() => {
    if (!selectedAccountId || !open) {
      setFirmaCuenta(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await mailService.getMailAccount(selectedAccountId);
        if (!cancelled) setFirmaCuenta(res.data?.firma || null);
      } catch (e) {
        if (!cancelled) setFirmaCuenta(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedAccountId, open]);

  const handleToggleHtml = () => {
    if (htmlMode) {
      // Volver a visual: aplicar el HTML al editor
      if (editor?.commands) {
        editor.commands.setContent(htmlSource || "<p></p>");
      }
      setHtmlMode(false);
    } else {
      // Ir a HTML: copiar el contenido actual al textarea
      setHtmlSource(editor?.getHTML() || "<p></p>");
      setHtmlMode(true);
    }
  };

  useEffect(() => {
    if (editor && !open) editor.setEditable(false);
  }, [open, editor]);

  useEffect(() => {
    if (!accountDropdownOpen) return;
    const handler = (e) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target)) {
        setAccountDropdownOpen(false);
        setAccountSearchText("");
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [accountDropdownOpen]);

  const addFiles = (files) => {
    const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024;
    const valid = [];
    for (const file of files) {
      if (attachments.length + valid.length >= MAX_ATTACHMENTS) {
        toast.warning(`Máximo ${MAX_ATTACHMENTS} archivos permitidos`);
        break;
      }
      if (file.size > maxSize) {
        toast.warning(`"${file.name}" supera los ${MAX_FILE_SIZE_MB} MB`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length) {
      setAttachments((prev) => [...prev, ...valid].slice(0, MAX_ATTACHMENTS));
    }
  };

  const handleFileSelect = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    const images = files.filter((f) => f.type.startsWith("image/"));
    const others = files.filter((f) => !f.type.startsWith("image/"));
    if (images.length && editor) {
      const file = images[0];
      const reader = new FileReader();
      reader.onload = () => {
        editor.chain().focus().setImage({ src: reader.result }).run();
      };
      reader.readAsDataURL(file);
    }
    if (others.length) addFiles(others);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!to.trim() && !cc.trim() && !bcc.trim()) {
      toast.warning("Ingresa al menos un destinatario en Para, CC o CCO");
      return;
    }
    setSending(true);
    try {
      let html = htmlMode ? htmlSource : (editor?.getHTML() || "");
      if (incluirFirma && firma?.trim()) {
        html += '<br/><div style="border-top:1px solid #ccc;margin-top:16px;padding-top:12px">' + firma.trim() + "</div>";
      }
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const text = tempDiv.textContent || tempDiv.innerText || "";
      if (!selectedAccountId) {
        toast.warning("Selecciona una cuenta para enviar");
        return;
      }
      await onSend({
        mailAccountId: selectedAccountId,
        to: to.trim(),
        cc: cc.trim(),
        bcc: bcc.trim(),
        subject,
        text,
        html,
        attachments,
      });
      onClose();
    } catch {
      // parent handles error
    } finally {
      setSending(false);
    }
  };

  const totalSize = attachments.reduce((sum, f) => sum + f.size, 0);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex: 9998 }}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="duration-150 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex min-h-full items-center justify-center p-6">
            <Transition.Child
              as={Fragment}
              enter="duration-200 ease-out"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="duration-150 ease-in"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-5xl transform rounded-xl bg-white shadow-2xl transition-all flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3 bg-gray-900 rounded-t-xl">
                  <div className="flex items-center gap-3">
                    <FaPaperPlane className="text-orange-400 text-sm" />
                    <h2 className="text-sm font-semibold text-white tracking-wide">
                      {replyData ? "Responder correo" : "Nuevo correo"}
                    </h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  >
                    <MdClose className="text-lg" />
                  </button>
                </div>

                {/* De: selector de cuenta */}
                <div ref={accountDropdownRef} className="relative">
                  <div className="px-6 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex-shrink-0">De</span>
                    {connectedList.length === 0 ? (
                      <p className="text-xs text-amber-600 font-medium">
                        Conecta al menos una cuenta para poder enviar correos
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => connectedList.length > 0 && setAccountDropdownOpen((v) => !v)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left hover:bg-gray-100 rounded px-2 py-1 -mx-2 -my-1 transition-colors"
                      >
                        <span className="text-xs font-semibold text-gray-700 truncate">
                          {(() => {
                            const acc = connectedList.find((a) => a.id === selectedAccountId) || connectedList[0];
                            if (!acc) return "";
                            const nombre = acc.nombre || acc.imapUser;
                            if (acc.nombre) {
                              return (
                                <>
                                  {nombre}{" - "}
                                  <span className="text-gray-400 font-normal">({acc.imapUser})</span>
                                </>
                              );
                            }
                            return acc.imapUser;
                          })()}
                        </span>
                        <FaChevronDown
                          className={`text-[10px] text-gray-400 flex-shrink-0 transition-transform ${accountDropdownOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}
                  </div>
                  {accountDropdownOpen && connectedList.length > 0 && (
                    <div className="absolute left-6 right-6 top-full mt-0.5 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-[240px] overflow-hidden flex flex-col">
                      <div className="p-2 border-b border-gray-100 flex-shrink-0">
                        <div className="relative">
                          <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
                          <input
                            type="text"
                            value={accountSearchText}
                            onChange={(e) => setAccountSearchText(e.target.value)}
                            placeholder="Buscar cuenta..."
                            className="w-full pl-7 pr-2 py-1.5 text-[11px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-[180px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                      {connectedList
                        .filter((acc) => {
                          const q = accountSearchText.trim().toLowerCase();
                          if (!q) return true;
                          const nombre = (acc.nombre || "").toLowerCase();
                          const email = (acc.imapUser || "").toLowerCase();
                          return nombre.includes(q) || email.includes(q);
                        })
                        .map((acc) => (
                        <button
                          key={acc.id}
                          type="button"
                          onClick={() => {
                            setSelectedAccountId(acc.id);
                            setAccountDropdownOpen(false);
                            setAccountSearchText("");
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-[11px] transition-colors ${
                            selectedAccountId === acc.id ? "bg-orange-50 text-orange-700" : "hover:bg-gray-50 text-gray-700"
                          }`}
                        >
                          <span
                            className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold rounded flex-shrink-0"
                            style={{
                              backgroundColor: acc.color || "#e5e7eb",
                              color: acc.color ? (acc.textColor || "#ffffff") : "#4b5563",
                            }}
                          >
                            {acc.nombre || acc.imapUser}
                          </span>
                          <span className="text-gray-500 truncate">{acc.imapUser}</span>
                        </button>
                      ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* To + Subject fields */}
                <div className="border-b border-gray-100">
                  <div className="flex items-center border-b border-gray-100">
                    <label className="w-20 pl-6 text-[11px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0">Para</label>
                    <input
                      type="text"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      placeholder="destinatario@ejemplo.com, otro@ejemplo.com"
                      className="flex-1 px-3 py-3 text-sm border-none focus:outline-none focus:ring-0 bg-transparent placeholder:text-gray-300"
                    />
                  </div>
                  <div className="flex items-center border-b border-gray-100">
                    <label className="w-20 pl-6 text-[11px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0">CC</label>
                    <input
                      type="text"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      placeholder="Copia (opcional), varios separados por coma"
                      className="flex-1 px-3 py-3 text-sm border-none focus:outline-none focus:ring-0 bg-transparent placeholder:text-gray-300"
                    />
                  </div>
                  <div className="flex items-center border-b border-gray-100">
                    <label className="w-20 pl-6 text-[11px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0">CCO</label>
                    <input
                      type="text"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      placeholder="Copia oculta (opcional), varios separados por coma"
                      className="flex-1 px-3 py-3 text-sm border-none focus:outline-none focus:ring-0 bg-transparent placeholder:text-gray-300"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="w-20 pl-6 text-[11px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0">Asunto</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Escribe el asunto del correo"
                      className="flex-1 px-3 py-3 text-sm border-none focus:outline-none focus:ring-0 bg-transparent placeholder:text-gray-300"
                    />
                  </div>
                </div>

                {/* TipTap Editor */}
                <div
                  className={`flex-1 overflow-y-auto min-h-[200px] relative [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${dragOver ? "ring-2 ring-inset ring-orange-400 bg-orange-50/30" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  {dragOver && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-orange-50/80 pointer-events-none">
                      <div className="flex flex-col items-center gap-2 text-orange-500">
                        <FaPaperclip className="text-3xl" />
                        <span className="text-sm font-semibold">Suelta los archivos aquí</span>
                      </div>
                    </div>
                  )}
                  <EditorToolbar editor={editor} htmlMode={htmlMode} onToggleHtml={handleToggleHtml} />
                  {htmlMode ? (
                    <div className="px-4 py-3">
                      <textarea
                        value={htmlSource}
                        onChange={(e) => setHtmlSource(e.target.value)}
                        placeholder="Escribe o pega tu HTML aquí. Ej: <p>Hola</p>, <strong>negrita</strong>, <a href='...'>enlace</a>"
                        className="w-full min-h-[260px] p-4 font-mono text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-y"
                        spellCheck={false}
                      />
                      <p className="mt-2 text-[10px] text-gray-400">
                        Puedes usar etiquetas HTML: p, strong, em, u, h1-h3, ul, ol, li, a, img, blockquote, br, etc.
                      </p>
                    </div>
                  ) : (
                    <div className="compose-tiptap-wrapper">
                      <EditorContent editor={editor} />
                    </div>
                  )}
                  <style>{`
                    .compose-tiptap-wrapper .tiptap {
                      min-height: 160px;
                      padding: 20px 24px;
                      font-family: 'Poppins', sans-serif;
                      font-size: 13px;
                      line-height: 1.7;
                      outline: none;
                    }
                    .compose-tiptap-wrapper .tiptap p.is-editor-empty:first-child::before {
                      color: #ccc;
                      content: attr(data-placeholder);
                      float: left;
                      height: 0;
                      pointer-events: none;
                    }
                    .compose-tiptap-wrapper .tiptap img {
                      max-width: 100%;
                      height: auto;
                      border-radius: 8px;
                    }
                    .compose-tiptap-wrapper .tiptap a {
                      color: #ff6400;
                      text-decoration: underline;
                    }
                    .compose-firma-preview p { margin: 0.25em 0; }
                    .compose-firma-preview a { color: #ff6400; text-decoration: underline; }
                    .compose-firma-preview img { max-width: 100%; height: auto; }
                  `}</style>
                </div>

                {/* Firma: selector + checkbox + preview */}
                {(hasFirmaCuenta || hasFirmaTrabajador) && (
                  <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/30">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={incluirFirma}
                        onChange={(e) => setIncluirFirma(e.target.checked)}
                        className="w-3.5 h-3.5 accent-orange-500 rounded"
                      />
                      <span className="text-[11px] font-semibold text-gray-700">Incluir firma</span>
                    </label>
                    <div className="mt-2 flex items-center gap-4">
                      <label className={`flex items-center gap-1.5 ${hasFirmaCuenta ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                        <input
                          type="radio"
                          name="tipoFirma"
                          checked={tipoFirma === "cuenta"}
                          onChange={() => setTipoFirma("cuenta")}
                          disabled={!hasFirmaCuenta}
                          className="w-3 h-3 accent-orange-500"
                        />
                        <span className="text-[11px] text-gray-600">
                          Firma de la cuenta
                          {!hasFirmaCuenta && <span className="text-[9px] text-gray-400 ml-1">(no configurada)</span>}
                        </span>
                      </label>
                      <label className={`flex items-center gap-1.5 ${hasFirmaTrabajador ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                        <input
                          type="radio"
                          name="tipoFirma"
                          checked={tipoFirma === "trabajador"}
                          onChange={() => setTipoFirma("trabajador")}
                          disabled={!hasFirmaTrabajador}
                          className="w-3 h-3 accent-orange-500"
                        />
                        <span className="text-[11px] text-gray-600">
                          Firma personal
                          {!hasFirmaTrabajador && <span className="text-[9px] text-gray-400 ml-1">(no configurada)</span>}
                        </span>
                      </label>
                    </div>
                    {incluirFirma && firma && (
                      <div className="mt-2 pt-3 border-t border-gray-200">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5">Vista previa de la firma</span>
                        <div
                          className="text-xs text-gray-700 prose prose-sm max-w-none compose-firma-preview overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                          style={{ maxHeight: 250 }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(firma) }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Attachments area */}
                {attachments.length > 0 && (
                  <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        {attachments.length} adjunto{attachments.length > 1 ? "s" : ""} ({formatFileSize(totalSize)})
                      </span>
                      <button
                        onClick={() => setAttachments([])}
                        className="text-[10px] text-red-400 hover:text-red-600 font-semibold transition-colors"
                      >
                        Quitar todos
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-[120px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                      {attachments.map((file, i) => (
                        <div
                          key={`${file.name}-${i}`}
                          className="flex items-center gap-2.5 px-3 py-2 bg-white rounded-lg border border-gray-200 group hover:border-gray-300 transition-colors"
                        >
                          <div className="text-base flex-shrink-0">{getFileIcon(file)}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-gray-700 truncate">{file.name}</p>
                            <p className="text-[9px] text-gray-400">{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            onClick={() => removeAttachment(i)}
                            className="p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-50 text-gray-300 hover:text-red-500 transition-all flex-shrink-0"
                          >
                            <FaTimes className="text-[9px]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer toolbar */}
                <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50/80 rounded-b-xl">
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={attachments.length >= MAX_ATTACHMENTS}
                      className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title={`Adjuntar archivos (máx. ${MAX_ATTACHMENTS} archivos, ${MAX_FILE_SIZE_MB} MB c/u)`}
                    >
                      <FaPaperclip className="text-xs" />
                      Adjuntar
                    </button>
                    <span className="text-[10px] text-gray-400">
                      {attachments.length > 0
                        ? `${attachments.length}/${MAX_ATTACHMENTS}`
                        : `Máx. ${MAX_FILE_SIZE_MB} MB por archivo`}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-[11px] font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Descartar
                    </button>
                    <button
                      onClick={handleSend}
                      disabled={(!to.trim() && !cc.trim() && !bcc.trim()) || !selectedAccountId || sending}
                      className="flex items-center gap-2 px-5 py-2 text-[11px] font-bold text-white bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
                    >
                      {sending ? (
                        <>
                          <FaSpinner className="animate-spin text-xs" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="text-[10px]" />
                          Enviar correo
                        </>
                      )}
                    </button>
                  </div>
                </div>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ComposeModal;
